import type Hls from 'hls.js';
import { getStoredAccessToken, withAccessToken } from '@auth/token';

export interface HlsPlaybackSession {
  destroy: () => void;
}

export interface StartHlsPlaybackOptions {
  video: HTMLVideoElement;
  sourceUrl: string;
  signal?: AbortSignal;
  /** Use for MediaMTX live HLS; timeline virtual playlists stay VOD-style. */
  lowLatencyMode?: boolean;
  onReady?: () => void;
  onFatalError: (message: string) => void;
  fatalErrorMessage?: string;
}

export function resetVideoElement(video: HTMLVideoElement): void {
  video.pause();
  video.removeAttribute('src');
  video.srcObject = null;
  video.load();
}

/**
 * Attach HLS.js (or native Safari HLS) to a video element and load `sourceUrl`.
 * Caller must call `destroy()` on the returned session when tearing down.
 */
export async function startHlsPlayback(
  options: StartHlsPlaybackOptions,
): Promise<HlsPlaybackSession> {
  const { default: HlsCtor } = await import('hls.js');
  let hls: Hls | null = null;
  let cancelled = false;

  const destroy = (): void => {
    cancelled = true;
    hls?.destroy();
    hls = null;
  };

  options.signal?.addEventListener('abort', destroy, { once: true });

  const fatalFallback = options.fatalErrorMessage ?? 'Failed to load stream';
  const sourceUrl = withAccessToken(options.sourceUrl);

  const onReady = (): void => {
    if (!cancelled) options.onReady?.();
  };

  const onFatal = (message: string): void => {
    if (!cancelled) options.onFatalError(message);
  };

  if (HlsCtor.isSupported()) {
    hls = new HlsCtor({
      enableWorker: false,
      lowLatencyMode: options.lowLatencyMode ?? false,
      xhrSetup: (xhr) => {
        const token = getStoredAccessToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
      },
    });
    hls.on(HlsCtor.Events.MEDIA_ATTACHED, () => {
      if (!cancelled) hls?.loadSource(sourceUrl);
    });
    hls.on(HlsCtor.Events.MANIFEST_PARSED, onReady);
    hls.on(HlsCtor.Events.ERROR, (_event, data) => {
      if (cancelled || !data.fatal) return;
      switch (data.type) {
        case HlsCtor.ErrorTypes.NETWORK_ERROR:
          hls?.startLoad();
          break;
        case HlsCtor.ErrorTypes.MEDIA_ERROR:
          hls?.recoverMediaError();
          break;
        default:
          onFatal(data.error?.message ?? fatalFallback);
      }
    });
    hls.attachMedia(options.video);
  } else if (options.video.canPlayType('application/vnd.apple.mpegurl')) {
    options.video.src = sourceUrl;
    options.video.addEventListener('loadedmetadata', onReady, { once: true });
    options.video.addEventListener(
      'error',
      () => {
        onFatal(fatalFallback);
      },
      { once: true },
    );
  } else {
    onFatal('This browser does not support HLS playback');
  }

  return { destroy };
}
